import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { TextField } from '@mui/material';
import AddNote from './AddNote';
import AddTask from './AddTask';
import MakeCall from './MakeCall';
import SendEmail from './SendEmail';
import AddMeeting from './AddMeeting';
import RightSidebar from '../../pages/SinglePage/RightSidebar';
import { resetActivityForm, setActiveActivityBox, setActivityLoading, createActivity, getObjectActivities } from '../../store/Activities';
import { openSoftphoneAndCall } from '../../store/Phone';
import '../../pages/SinglePage/ActivityCard/activities.css';

const Activities = () => {

    const dispatch = useDispatch();
    const { singlePageData } = useSelector(state => state['SinglePage']);
    const { activeActivityBox, objectData } = useSelector(state => state['Activities']);

    // Local state only for UI elements not in Redux
    let [isActiveExpande, setIsActiveExpande] = useState(false);
    const [showContactSelector, setShowContactSelector] = useState(false);
    const [searchText, setSearchText] = useState('');

    // Get contact display name inline
    const getDisplayName = (c) => `${c.firstName || c.firstname || ''} ${c.lastName || c.lastname || ''}`.trim() || 'Unknown Contact';

    // Get contacts with phone numbers for calls (with optional search filter)
    const getContactsWithPhone = (search = '') => {
        if (!singlePageData?.contacts || !Array.isArray(singlePageData.contacts)) return [];
        return singlePageData.contacts.filter(c => {
            if (!c.phone) return false;
            if (!search) return true;
            const name = getDisplayName(c).toLowerCase();
            return name.includes(search.toLowerCase()) || c.phone.toLowerCase().includes(search.toLowerCase());
        });
    };

    // Handle selecting a contact to call
    const handleSelectContactToCall = (contact) => {
        setShowContactSelector(false);
        setSearchText('');
        dispatch(openSoftphoneAndCall({
            phone: contact.phone,
            contact: { name: getDisplayName(contact), _id: contact._id, objectType: 'contacts', parentId: singlePageData?._id, parentType: singlePageData?.objectType },
            justOpenDialpad: false
        }));
        dispatch(setActiveActivityBox(null));
    };

    // When user clicks call button, check if we need to show contact selector
    useEffect(() => {
        if (activeActivityBox === 'call') {
            const contactsWithPhone = getContactsWithPhone();
            const objectType = singlePageData?.objectType;

            // If on deal/company page with multiple contacts with phone, show selector
            if ((objectType === 'deals' || objectType === 'companies') && contactsWithPhone.length > 1) {
                setShowContactSelector(true);
            } else if ((objectType === 'deals' || objectType === 'companies') && contactsWithPhone.length === 1) {
                // Single contact - call directly
                handleSelectContactToCall(contactsWithPhone[0]);
            } else {
                // Contact page or deal/company without associated contacts - open dialpad
                const phone = singlePageData?.['phone'] || null;
                const contact = singlePageData ? { name: singlePageData['name'] || singlePageData['firstName'] + ' ' + singlePageData['lastName'], _id: singlePageData['_id'], objectType: singlePageData['objectType']} : null;
                dispatch(openSoftphoneAndCall({ phone, contact, justOpenDialpad: true }));
                dispatch(setActiveActivityBox(null));
            }
        }
    }, [activeActivityBox, singlePageData, dispatch]);

    const clearActivityForm = () => {
        dispatch(resetActivityForm());
        dispatch(setActivityLoading(false));
        dispatch(setActiveActivityBox(null));
    }

    const onCreateActivity = (activity) => dispatch(createActivity({ data: activity, successCallback: () => { 
        getActivities();
        clearActivityForm();
    }}));

    const getActivities = () => {
        if(singlePageData['objectType'])
            dispatch(getObjectActivities({ objectType: singlePageData['objectType'], objectId: singlePageData['_id'] }));
    }

    // Handle closing contact selector
    const handleCloseContactSelector = () => {
        setShowContactSelector(false);
        setSearchText('');
        dispatch(setActiveActivityBox(null));
    };

    // Contact selector dropdown for calls - positioned under call icon
    if (showContactSelector && activeActivityBox === 'call') {
        return (
            <div className="contact-selector-overlay" onClick={handleCloseContactSelector}>
                <div className="contact-selector-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="contact-selector-header">
                        <div>
                            <h3>Select Contact to Call</h3>
                            <p>{getContactsWithPhone().length} contact{getContactsWithPhone().length !== 1 ? 's' : ''} with phone numbers</p>
                        </div>
                        <button className="contact-selector-close" onClick={handleCloseContactSelector}>
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <path d="M13 1L1 13M1 1L13 13" stroke="#6b7280" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                        </button>
                    </div>

                    <TextField fullWidth placeholder="Search by name or phone..." value={searchText} onChange={(e) => setSearchText(e.target.value)} size="small" sx={{ marginBottom: '16px' }} />

                    <div className="contact-selector-list">
                        {getContactsWithPhone(searchText).length > 0 ? (
                            getContactsWithPhone(searchText).map((contact) => (
                                <div key={contact._id} className="contact-selector-item" onClick={() => handleSelectContactToCall(contact)}>
                                    <div className="contact-selector-item-info">
                                        <div className="contact-selector-item-name">{getDisplayName(contact)}</div>
                                        <div className="contact-selector-item-phone">
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                            {contact.phone}
                                        </div>
                                    </div>
                                    <div className="contact-selector-call-icon">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                            <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="contact-selector-empty">
                                {searchText ? 'No contacts found matching your search' : 'No contacts with phone numbers'}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // Don't render if no activity box is active
    if (!activeActivityBox)
        return null;
    // Skip rendering for 'call' - it opens Dialpad via useEffect above
    else if(activeActivityBox === 'call')
        return null;
    // SMS/WhatsApp - handled by RightSidebar in SinglePage via isRightSidebar state; associations already set in Redux
    else if(activeActivityBox === 'sms' || activeActivityBox === 'whatsapp')
        return null;
    else if(activeActivityBox === 'note')
        return <AddNote clearActivityForm={clearActivityForm} onCreateActivity={onCreateActivity} isActiveExpande={isActiveExpande} setIsActiveExpande={setIsActiveExpande} />
    else if(activeActivityBox === 'task')
        return <AddTask clearActivityForm={clearActivityForm} onCreateActivity={onCreateActivity} isActiveExpande={isActiveExpande} setIsActiveExpande={setIsActiveExpande} />
    else if(activeActivityBox === 'mail' || activeActivityBox === 'email')
        return <SendEmail clearActivityForm={clearActivityForm} onCreateActivity={onCreateActivity} isActiveExpande={isActiveExpande} setIsActiveExpande={setIsActiveExpande} />
    else if(activeActivityBox === 'schedule' || activeActivityBox === 'meeting')
        return <AddMeeting clearActivityForm={clearActivityForm} onCreateActivity={onCreateActivity} isActiveExpande={isActiveExpande} setIsActiveExpande={setIsActiveExpande} />
    return null;
};

export default Activities;